import React, { useState, useCallback } from 'react';
import { FiX, FiUpload, FiMap, FiEye, FiCheck } from 'react-icons/fi';
import { UploadStep } from './UploadStep';
import { ColumnMappingStep } from './ColumnMappingStep';
import { PreviewStep } from './PreviewStep';
import { SummaryStep } from './SummaryStep';
import { ImportProgressDialog } from './ImportProgressDialog';

interface CsvRow {
  [key: string]: string;
}

interface RowError {
  row: number;
  reason: string;
  data: any;
}

interface ImportState {
  rows: CsvRow[];
  mapping: Record<string, string>;
  errors: RowError[];
  duplicates: number[];
  skipped: number[];
  batchId?: string;
  currentStep: number;
}

interface ImportWizardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const STEPS = [
  { id: 'upload', title: 'Upload CSV', icon: FiUpload },
  { id: 'mapping', title: 'Map Columns', icon: FiMap },
  { id: 'preview', title: 'Preview Data', icon: FiEye },
  { id: 'summary', title: 'Ready to Import', icon: FiCheck },
];

export const ImportWizardDrawer: React.FC<ImportWizardDrawerProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [importState, setImportState] = useState<ImportState>({
    rows: [],
    mapping: {},
    errors: [],
    duplicates: [],
    skipped: [],
    currentStep: 0
  });

  const [showProgress, setShowProgress] = useState(false);

  const handleUploadComplete = useCallback((rows: CsvRow[], mapping: Record<string, string>) => {
    setImportState(prev => ({
      ...prev,
      rows,
      mapping,
      currentStep: 1
    }));
  }, []);

  const handleMappingComplete = useCallback((mapping: Record<string, string>, errors: RowError[]) => {
    setImportState(prev => ({
      ...prev,
      mapping,
      errors,
      currentStep: 2
    }));
  }, []);

  const handlePreviewComplete = useCallback((errors: RowError[], duplicates: number[]) => {
    setImportState(prev => ({
      ...prev,
      errors,
      duplicates,
      currentStep: 3
    }));
  }, []);

  const handleImportStart = useCallback(async (batchId: string) => {
    setImportState(prev => ({
      ...prev,
      batchId
    }));
    setShowProgress(true);
  }, []);

  const handleImportComplete = useCallback(() => {
    setShowProgress(false);
    onComplete?.();
    onClose();
  }, [onComplete, onClose]);

  const handleClose = useCallback(() => {
    if (importState.currentStep > 0) {
      // Reset state when closing
      setImportState({
        rows: [],
        mapping: {},
        errors: [],
        duplicates: [],
        skipped: [],
        currentStep: 0
      });
    }
    onClose();
  }, [importState.currentStep, onClose]);

  const renderCurrentStep = () => {
    switch (importState.currentStep) {
      case 0:
        return (
          <UploadStep
            onComplete={handleUploadComplete}
            onClose={handleClose}
          />
        );
      case 1:
        return (
          <ColumnMappingStep
            rows={importState.rows}
            mapping={importState.mapping}
            onComplete={handleMappingComplete}
            onBack={() => setImportState(prev => ({ ...prev, currentStep: 0 }))}
            onClose={handleClose}
          />
        );
      case 2:
        return (
          <PreviewStep
            rows={importState.rows}
            mapping={importState.mapping}
            onComplete={handlePreviewComplete}
            onBack={() => setImportState(prev => ({ ...prev, currentStep: 1 }))}
            onClose={handleClose}
          />
        );
      case 3:
        return (
          <SummaryStep
            rows={importState.rows}
            mapping={importState.mapping}
            errors={importState.errors}
            duplicates={importState.duplicates}
            onImport={handleImportStart}
            onBack={() => setImportState(prev => ({ ...prev, currentStep: 2 }))}
            onClose={handleClose}
          />
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full sm:w-[600px] bg-white dark:bg-neutral-900 shadow-xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">
              Import Customers
            </h2>
            {/* Step indicator */}
            <div className="flex items-center space-x-2">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === importState.currentStep;
                const isCompleted = index < importState.currentStep;
                
                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`
                      flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                      ${isActive 
                        ? 'bg-blue-600 text-white' 
                        : isCompleted 
                          ? 'bg-green-600 text-white'
                          : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400'
                      }
                    `}>
                      {isCompleted ? (
                        <FiCheck size={16} />
                      ) : (
                        <Icon size={16} />
                      )}
                    </div>
                    {index < STEPS.length - 1 && (
                      <div className={`
                        w-8 h-0.5 mx-2
                        ${isCompleted ? 'bg-green-600' : 'bg-neutral-200 dark:bg-neutral-700'}
                      `} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            aria-label="Close import wizard"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {renderCurrentStep()}
        </div>
      </div>

      {/* Progress Dialog */}
      {showProgress && importState.batchId && (
        <ImportProgressDialog
          batchId={importState.batchId}
          onClose={() => setShowProgress(false)}
          onComplete={handleImportComplete}
        />
      )}
    </>
  );
}; 